import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase, isSupabaseConfigured } from '@/integrations/supabase/client';

// Types for our data structures
export interface Setting {
  id: string;
  key: string;
  value_ar: string | null;
  value_en: string | null;
}

export interface MediaItem {
  id: string;
  title_ar: string | null;
  title_en: string | null;
  alt_ar: string | null;
  alt_en: string | null;
  file_url: string;
  file_type: string;
  category: string | null;
  sort_order: number;
}

export interface Page {
  id: string;
  slug: string;
  title_ar: string;
  title_en: string;
  content_ar: string | null;
  content_en: string | null;
  meta_title_ar: string | null;
  meta_title_en: string | null;
  meta_description_ar: string | null;
  meta_description_en: string | null;
  featured_image_url: string | null;
}

export interface Service {
  id: string;
  title_ar: string;
  title_en: string;
  description_ar: string | null;
  description_en: string | null;
  icon_url: string | null;
  featured_image_url: string | null;
  slug: string;
  sort_order: number;
}

export interface HomeSection {
  id: string;
  section_key: string;
  title_ar: string | null;
  title_en: string | null;
  subtitle_ar: string | null;
  subtitle_en: string | null;
  content_ar: string | null;
  content_en: string | null;
  button_text_ar: string | null;
  button_text_en: string | null;
  button_link: string | null;
  background_url: string | null;
  sort_order: number;
}

export interface Sector {
  id: string;
  name_ar: string;
  name_en: string;
  slug: string;
  // Short description (excerpt) fields – used for hero subtitle on detail pages
  short_description_ar?: string;
  short_description_en?: string;
  description_ar?: string;
  description_en?: string;
  icon_url?: string;
  banner_image_url?: string | null;
  featured_image_url?: string | null;
  gallery_urls?: string[];
  sort_order?: number;
  is_active?: boolean;
  company_count?: number;
}

export interface Company {
  id: string;
  name_ar: string;
  name_en: string;
  slug: string;
  description_ar?: string;
  description_en?: string;
  logo_url?: string;
  icon_url?: string; // New icon field
  banner_image_url?: string; // New banner field
  website_url?: string;
  contact_info?: {
    email?: string;
    phone?: string;
    address?: string;
    [key: string]: unknown;
  };
  gallery_urls?: string[];
  sector_id?: string;
  sort_order?: number;
  is_active?: boolean;
}

// Custom hooks for fetching data
export const useSettings = () => {
  const [settings, setSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        if (!isSupabaseConfigured) {
          setSettings([]);
          return;
        }
        const { data, error } = await supabase
          .from('settings')
          .select('*');

        if (error) throw error;
        setSettings(data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const getSetting = (key: string, language: 'ar' | 'en' = 'ar') => {
    const setting = settings.find(s => s.key === key);
    if (!setting) return '';
    return language === 'ar' ? setting.value_ar || '' : setting.value_en || '';
  };

  return { settings, loading, error, getSetting };
};

export const useMediaLibrary = (category?: string) => {
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        if (!isSupabaseConfigured) {
          setMedia([]);
          return;
        }
        let query = supabase
          .from('media_library')
          .select('*')
          .eq('is_active', true)
          .order('sort_order', { ascending: true });

        if (category) {
          query = query.eq('category', category);
        }

        const { data, error } = await query;

        if (error) throw error;
        setMedia(data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchMedia();
  }, [category]);

  return { media, loading, error };
};

export const usePages = (slug?: string) => {
  const [pages, setPages] = useState<Page[]>([]);
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPages = async () => {
      try {
        if (!isSupabaseConfigured) {
          setPages([]);
          setPage(null);
          return;
        }
        let query = supabase
          .from('pages')
          .select('*')
          .eq('is_active', true)
          .order('sort_order', { ascending: true });

        if (slug) {
          query = query.eq('slug', slug);
          const { data, error } = await query.single();
          if (error) throw error;
          const p = data as Partial<Page>;
          setPage({
            id: String(p.id ?? ''),
            slug: String(p.slug ?? ''),
            title_ar: String(p.title_ar ?? ''),
            title_en: String(p.title_en ?? ''),
            content_ar: p.content_ar ?? '',
            content_en: p.content_en ?? '',
            meta_title_ar: p.meta_title_ar ?? '',
            meta_title_en: p.meta_title_en ?? '',
            meta_description_ar: p.meta_description_ar ?? '',
            meta_description_en: p.meta_description_en ?? '',
            featured_image_url: p.featured_image_url ?? ''
          });
        } else {
          const { data, error } = await query;
          if (error) throw error;
          const normalized: Page[] = (data || []).map((pg) => {
            const p = pg as Partial<Page>;
            return {
              id: String(p.id ?? ''),
              slug: String(p.slug ?? ''),
              title_ar: String(p.title_ar ?? ''),
              title_en: String(p.title_en ?? ''),
              content_ar: p.content_ar ?? '',
              content_en: p.content_en ?? '',
              meta_title_ar: p.meta_title_ar ?? '',
              meta_title_en: p.meta_title_en ?? '',
              meta_description_ar: p.meta_description_ar ?? '',
              meta_description_en: p.meta_description_en ?? '',
              featured_image_url: p.featured_image_url ?? ''
            };
          });
          setPages(normalized);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchPages();
  }, [slug]);

  return { pages, page, loading, error };
};

export const useServices = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        if (!isSupabaseConfigured) {
          setServices([]);
          return;
        }
        const { data, error } = await supabase
          .from('services')
          .select('*')
          .eq('is_active', true)
          .order('sort_order', { ascending: true });

        if (error) throw error;
        setServices(data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  return { services, loading, error };
};

export const useHomeSections = () => {
  const [sections, setSections] = useState<HomeSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSections = async () => {
      try {
        if (!isSupabaseConfigured) {
          setSections([]);
          return;
        }
        const { data, error } = await supabase
          .from('home_sections')
          .select('*')
          .eq('is_active', true)
          .order('sort_order', { ascending: true });

        if (error) throw error;
        setSections(data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchSections();
  }, []);

  const getSection = (sectionKey: string) => {
    return sections.find(s => s.section_key === sectionKey);
  };

  return { sections, loading, error, getSection };
};

// Public Sectors hook for frontend (non-admin) - Optimized and simplified
export const usePublicSectors = () => {
  return useQuery({
    queryKey: ['sectors', 'public'],
    queryFn: async () => {
      // Simple sectors query without complex joins
      const { data: sectors, error: sectorsError } = await supabase
        .from('sectors')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (sectorsError) {
        throw sectorsError;
      }

      if (!sectors || sectors.length === 0) {
        return [];
      }

      // Get company counts in a separate, efficient query
      const { data: companyCounts, error: countError } = await supabase
        .from('companies')
        .select('sector_id')
        .eq('is_active', true);

      if (countError) {
        console.warn('Could not fetch company counts:', countError);
        // Return sectors without counts rather than failing
        return sectors.map(sector => ({ ...sector, company_count: 0 }));
      }

      // Count companies per sector
      const countMap = (companyCounts || []).reduce((acc: Record<string, number>, company) => {
        if (company.sector_id) {
          acc[company.sector_id] = (acc[company.sector_id] || 0) + 1;
        }
        return acc;
      }, {});

      // Add company counts to sectors
      const sectorsWithCount = sectors.map(sector => ({
        ...sector,
        company_count: countMap[sector.id] || 0
      }));
      
      return sectorsWithCount as Sector[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    refetchOnWindowFocus: false
  });
};

export const useSector = (slug: string) => {
  return useQuery({
    queryKey: ['sector', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sectors')
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .single();

      if (error) throw error;
      return data as unknown as Sector;
    },
    enabled: !!slug
  });
};

export const useCompaniesBySector = (sectorId: string) => {
  return useQuery({
    queryKey: ['companies', 'sector', sectorId],
    queryFn: async () => {
      if (!sectorId) return [];
      
      const { data, error } = await supabase
        .from('companies')
        .select(`
          id,
          name_en,
          name_ar,
          description_en,
          description_ar,
          logo_url,
          icon_url,
          banner_image_url,
          website_url,
          gallery_urls,
          sector_id,
          slug,
          sort_order,
          created_at,
          updated_at
        `)
        .eq('sector_id', sectorId)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data as unknown as Company[];
    },
    enabled: !!sectorId
  });
};

export const useCompany = (slug: string) => {
  return useQuery({
    queryKey: ['company', slug],
    queryFn: async () => {
      if (!slug) return null;
      
      const { data, error } = await supabase
        .from('companies')
        .select(`
          id,
          name_en,
          name_ar,
          description_en,
          description_ar,
          logo_url,
          icon_url,
          banner_image_url,
          website_url,
          contact_info,
          gallery_urls,
          sector_id,
          slug,
          sort_order,
          created_at,
          updated_at,
          sectors (
            id,
            name_ar,
            name_en,
            slug
          )
        `)
        .eq('slug', slug)
        .eq('is_active', true)
        .single();

      if (error) throw error;
      type CompanyWithSector = Company & { sectors?: { id: string; name_ar: string; name_en: string; slug: string } };
      return data as unknown as CompanyWithSector;
    },
    enabled: !!slug
  });
};

// Hook for submitting contact form
export const useContactSubmission = () => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitContact = async (data: {
    name: string;
    email: string;
    phone?: string;
    message: string;
  }) => {
    setSubmitting(true);
    setError(null);

    try {
      // Use secure Edge Function instead of direct database insert
      const { data: result, error } = await supabase.functions.invoke('submit-contact', {
        body: data
      });

      if (error) throw error;
      if (result?.error) throw new Error(result.error);
      
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  return { submitContact, submitting, error };
};

// Hook للحصول على الشركاء
export const usePartners = () => {
  const { data: partners, isLoading, error } = useQuery({
    queryKey: ['partners'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('partners')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  return { data: partners, isLoading, error };
};

// Hook للحصول على المشاريع
export const useProjects = () => {
  const { data: projects, isLoading, error } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          company:companies(name_ar, name_en, logo_url),
          sector:sectors(name_ar, name_en, icon_url)
        `)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  return { data: projects, isLoading, error };
};

// Hook للحصول على مشروع واحد بالـ slug
export const useProject = (slug: string) => {
  const { data: project, isLoading, error } = useQuery({
    queryKey: ['project', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          company:companies(name_ar, name_en, logo_url, website_url),
          sector:sectors(name_ar, name_en, icon_url)
        `)
        .eq('slug', slug)
        .eq('is_active', true)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  return { data: project, isLoading, error };
};

// Hook للحصول على المشاريع حسب الشركة
export const useProjectsByCompany = (companyId: string) => {
  const { data: projects, isLoading, error } = useQuery({
    queryKey: ['projects-by-company', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data;
    },
    enabled: !!companyId,
  });

  return { data: projects, isLoading, error };
};